import {
  type Dispatch,
  forwardRef,
  type SetStateAction,
  useEffect,
  useImperativeHandle,
} from "react";

import { Spinner, useToast } from "@hightouch/htui";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";

import { FormkitProvider } from "src/formkit/components/formkit-context";
import {
  type SourceDefinition,
  useFormkitSourceDefinitionQuery,
  useFormkitSourceValidationQuery,
} from "src/graphql";

import { IPWhitelistMessage } from "src/components/sources/ip-whitelist-message";
import type { SyncEngineProps } from "./form-method";
import { SelectedMethodForm } from "src/components/selected-method-form";
import { Form } from "src/formkit/components";
import type { SourceConfig } from "src/utils/sources";
import { useFlags } from "launchdarkly-react-client-sdk";

/**
 * Props shared between source and event destination forms.
 */
export type SharedFormProps = {
  definition: SourceDefinition;
  tunnelId: string | null | undefined;
  setTunnelId: (tunnelId: string | null | undefined) => void;
  config: Record<string, unknown> | undefined;
  setConfig: Dispatch<SetStateAction<Record<string, unknown> | undefined>>;
  credentialId: string | undefined;
  setCredentialId: (credential: string) => void;
  hideSecret?: boolean;
  onSubmit?: () => Promise<void>;
  /** Whether to disable authentication method radio selection. */
  disableAuthMethod?: boolean;
  /** Function called whenever OAuth 'connect' button is clicked. */
  onConnectClick?(definition: SourceDefinition): void;
  isSetup: boolean;
};

type SourceFormProps = {
  sourceId: string | undefined;
};

type FormProps = SharedFormProps & SourceFormProps & SyncEngineProps;

export type SourceValidationContext = {
  sourceDefinition: SourceDefinition;
  sourceId?: string | null | undefined;
  isSetup: boolean;
  tunnel?: string | null | undefined;
  credentialId?: string | null | undefined;
};

export type SourceFormRef = {
  validate: (
    config: SourceConfig,
    context: SourceValidationContext,
  ) => Promise<boolean>;
};

export const SourceForm = forwardRef<SourceFormRef, FormProps>(
  (
    {
      definition,
      setConfig,
      config,
      sourceId,
      isSetup,
      tunnelId,
      setTunnelId,
      credentialId,
      setCredentialId,
      onSubmit,
      disableAuthMethod,
      onConnectClick,
      lightningEnabled,
      setLightningEnabled,
      plannerDatabase,
      setPlannerDatabase,
      hasSetupLightning,
      lightningSchemaMode,
      setLightningSchemaMode,
      schema,
      setSchema,
      warehouseExportStage,
      setWarehouseExportStage,
      showWarehouseExportStage,
    },
    ref,
  ) => {
    const { queryRoutingSnowflakeForm, queryRoutingDatabricksForm } =
      useFlags();
    const client = useQueryClient();
    const { toast } = useToast();
    const { data, isLoading } = useFormkitSourceDefinitionQuery({
      type: definition?.type,
    });

    const setupMethods = data?.formkitSourceDefinition;
    const context = {
      sourceDefinition: definition,
      sourceId,
      isSetup,
      tunnel: tunnelId,
      credentialId,
    };

    const methods = useForm();

    const validate = async (
      config: SourceConfig,
      context: SourceValidationContext,
    ) => {
      const variables = {
        type: context.sourceDefinition?.type,
        config,
        context: {
          ...context,
          featureFlags: {
            queryRoutingSnowflakeForm,
            queryRoutingDatabricksForm,
          },
        },
      };
      const response = await client.fetchQuery({
        queryFn: useFormkitSourceValidationQuery.fetcher(variables),
        queryKey: useFormkitSourceValidationQuery.getKey(variables),
      });

      const errors = response.formkitSourceValidation;
      if (typeof errors === "object" && Object.keys(errors).length) {
        methods.clearErrors();
        Object.entries(errors).forEach(([key, message]) => {
          methods.setError(key, { message: String(message) });
        });
        return false;
      }
      return true;
    };

    const { methodKey, ...configWithoutMethodKey } = config ?? {};

    const setMethodKey = (methodKey: string) => {
      setConfig({ methodKey });
    };

    const setConfigWithoutMethodKey = (value: Record<string, unknown>) => {
      setConfig((prev) => ({ ...value, methodKey: prev?.methodKey }));
    };

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      // XXX: Make sure that the config is not already set before we reset it.
      if (
        configWithoutMethodKey &&
        Object.keys(configWithoutMethodKey).length
      ) {
        methods.reset(configWithoutMethodKey, { keepDefaultValues: true });
      }
    }, [Boolean(config)]);

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      // Always setup the setup method to the first one.
      const initialSetupMethod = setupMethods?.[0];

      if (initialSetupMethod && !config?.methodKey) {
        setConfig((prev) => ({ ...prev, methodKey: initialSetupMethod.key }));
      }
    }, [config, definition.type, setupMethods?.length]);

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      // XXX: This useEffect watches fields and sets config state in the parent.
      // We use this pattern because (unlike the sync form) the parent component
      // needs to know the config to render specific UI elements (like the continue
      // button) and for repeat testing in the testing step.

      const fieldWatcher = methods.watch((value) => {
        setConfigWithoutMethodKey(value);
      });
      return () => fieldWatcher.unsubscribe();
    }, [methods.watch]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (setupMethods?.length === 0) {
        if (typeof onSubmit === "function") {
          return await onSubmit();
        }

        return;
      }

      const isValid = await validate(config ?? {}, context);
      if (!isValid) {
        toast({
          id: "save-source-config",
          title: "There is an error in your source configuration",
          variant: "error",
        });
      } else {
        if (typeof onSubmit === "function") {
          await onSubmit();
        }
      }
    };

    useImperativeHandle(ref, () => ({
      validate,
    }));

    if (!setupMethods || !Array.isArray(setupMethods) || isLoading)
      return <Spinner size="lg" m="auto" />;

    return (
      <FormkitProvider {...context} validate={validate}>
        <FormProvider {...methods}>
          <Form>
            <SelectedMethodForm
              config={config}
              credentialId={credentialId}
              definition={definition}
              disableAuthMethod={disableAuthMethod}
              hasSetupLightning={hasSetupLightning}
              isSetup={isSetup}
              lightningEnabled={lightningEnabled}
              methodKey={String(methodKey)}
              plannerDatabase={plannerDatabase}
              setCredentialId={setCredentialId}
              setLightningEnabled={setLightningEnabled}
              setMethodKey={setMethodKey}
              setPlannerDatabase={setPlannerDatabase}
              setTunnelId={setTunnelId}
              setupMethods={setupMethods}
              sourceId={sourceId}
              tunnelId={tunnelId}
              onConnectClick={onConnectClick}
              schema={schema}
              setSchema={setSchema}
              lightningSchemaMode={lightningSchemaMode}
              setLightningSchemaMode={setLightningSchemaMode}
              warehouseExportStage={warehouseExportStage}
              setWarehouseExportStage={setWarehouseExportStage}
              showWarehouseExportStage={showWarehouseExportStage}
            />
            {definition.supportsIpFiltering && <IPWhitelistMessage />}
            <form hidden id="source-form" onSubmit={handleSubmit} />
          </Form>
        </FormProvider>
      </FormkitProvider>
    );
  },
);

SourceForm.displayName = "SourceForm";
